import { Card, Col, Popover, Row } from 'antd'
import cx from 'classnames'
import _ from 'lodash'
import momentTZ from 'moment-timezone'
import pluralize from 'pluralize'
import React from 'react'
import { I18n } from 'react-redux-i18n'
import { CPLAttributes, DCPDeliveryType, DCPStatuses, KDMStatuses, ShipmentStatuses } from '../../common/enums'

import { formatDateTime, getDuration } from '../../common/util/helper'
import { naturalCompare } from '../../common/util/string'
import ChevronDown from '../../icons/chevron-down.svg'
import ChevronRight from '../../icons/chevron-right.svg'

import DownloadKDMIcon from '../../icons/download-kdm.svg'
import DownloadIcon from '../../icons/download.svg'
import DriveIcon from '../../icons/drive.svg'
import EmailIcon from '../../icons/email.svg'
import LoaderIcon from '../../icons/loading.svg'
import CompositionAttributes from './CompositionAttributes'
import CompositionTheatreItem from './CompositionTheatreItem'
import DCPInfo from './DCPInfo'
import KDMInfo from './KDMInfo'

import MarkerInfo from './MarkerInfo'
import { DEVICE_MANAGER_ROLE } from '../../common/config'

class Composition extends React.Component {
  constructor (props) {
    super(props)

    this.state = {
      loadingTheatreId: null
    }
  }

  getDurationFromEnd (compositionDuration, duration) {
    return getDuration(parseInt(compositionDuration, 10) - parseInt(duration, 10))
  }

  showCreditOffset (compositionDuration, duration) {
    return getDuration(duration) + ' (-' + this.getDurationFromEnd(compositionDuration, duration) +
      ')'
  }

  showKdmValidity (kdmSummary) {
    if (kdmSummary) {
      const statuses = Object.keys(kdmSummary.statuses)
      if (statuses.find(k => k.toLowerCase() === KDMStatuses.inprogress)) {
        return statuses.length > 1
      }
      return true
    }
    return false
  }

  showViewCreditOffset (composition) {
    const {name, markers, duration} = composition
    this.props.showModal(
      I18n.t('compositions.creditOffset'),
      <MarkerInfo
        compositionName={name} markers={markers} compositionDuration={duration}
        getDurationFromEnd={this.getDurationFromEnd.bind(this)}
      />,
      '50%'
    )
  }

  showDCPInfo (theatre) {
    const {composition: cpl, loadTheatreDetails} = this.props
    this.setState({loadingTheatreId: theatre.id})
    return loadTheatreDetails(theatre.id, cpl.id).then(() => {
      this.setState({loadingTheatreId: null})
      const {composition, showModal} = this.props
      const updatedTheatre = composition.theatres.find(t => t.id === theatre.id)
      showModal(
        I18n.t('compositions.dcpStatusTitle'),
        <DCPInfo composition={composition} theatre={updatedTheatre}/>,
        '70%'
      )
    }).catch(() => {
      this.setState({loadingTheatreId: null})
    })
  }

  showKDMInfo (theatre, kdmGenerationCompleted) {
    const {composition: cpl, loadTheatreDetails, user} = this.props
    return loadTheatreDetails(theatre.id, cpl.id).then(() => {
      const {composition, sendKDMEmail, showModal} = this.props
      const updatedTheatre = composition.theatres.find(t => t.id === theatre.id)
      showModal(
        I18n.t('compositions.kdmDetails'),
        <KDMInfo
          composition={composition} theatre={updatedTheatre}
          kdmGenerationCompleted={kdmGenerationCompleted}
          downloadKDMS={() => this.downloadKDMS()} sendKDMEmail={sendKDMEmail} user={user}/>,
        '70%'
      )
    })
  }

  renderKDMStatusSummary (kdmStatus, kdmValidity) {
    let statusSummary = []
    let current = 1
    if (kdmStatus[KDMStatuses.inprogress]) {
      statusSummary.push(<span key={current}
                               className='qw-bold inline-block qw-align-middle inprogress'>{I18n.t('compositions.kdmStatus.inprogress')}</span>)
      current += 1
    }

    for (const status in kdmStatus) {
      if (status !== KDMStatuses.inprogress) {
        statusSummary.push(<span key={current}
                                 className={`qw-bold inline-block qw-align-middle ${status.toLowerCase()}`}>{current >
        1 &&
        <span>&nbsp;&bull;&nbsp;</span>}{kdmStatus[status]} {I18n.t(`compositions.kdmStatus.${status.toLowerCase()}`)}</span>)
        current += 1
      }
    }

    const {timezone, validFrom, validTill, isMultiple} = kdmValidity
    if (validFrom && validTill) {
      statusSummary.push(<span key={current}
                               className='block qw-align-middle'>{formatDateTime(momentTZ.tz(validFrom, timezone))} to {formatDateTime(momentTZ.tz(validTill, timezone))}</span>)
    } else if (isMultiple) {
      statusSummary.push(<span key={current}
                               className='block qw-align-middle'>{I18n.t('compositions.multipleValidities')}</span>)
    }

    return statusSummary
  }

  getTheatreKdmStatus (theatre, kdmStatus) {
    let kdmGenerationCompleted = true
    let kdmIssuedScreenCount = 0
    const kdmSummaryStatus = theatre.kdmSummary ? theatre.kdmSummary.statuses : [];
    [KDMStatuses.success, KDMStatuses.generated, KDMStatuses.emailed].forEach(key => {
      if (kdmSummaryStatus[key]) {
        kdmIssuedScreenCount += kdmSummaryStatus[key]
      }
    })

    Object.keys(kdmSummaryStatus).forEach(key => {
      const statusKey = key.toLowerCase()
      if (kdmStatus[statusKey]) {
        kdmStatus[statusKey] += kdmSummaryStatus[key]
      } else {
        kdmStatus[statusKey] = kdmSummaryStatus[key]
      }
    })

    if (kdmIssuedScreenCount && !!kdmStatus[KDMStatuses.inprogress]) {
      kdmGenerationCompleted = false
    }

    return kdmGenerationCompleted
  }

  getKDMSummaryData () {
    const {composition: {theatres}} = this.props

    const kdmStatus = {}
    let kdmGenerationCompleted = true
    let kdmValidity = {
      validFrom: '',
      validTill: '',
      timezone: '',
      isMultiple: false
    }
    theatres.forEach(theatre => {
      const {kdmSummary, timezone} = theatre
      const {validFrom, validTill} = kdmSummary || {}
      const showValidity = this.showKdmValidity(kdmSummary)
      if (showValidity && validFrom && validTill && !kdmValidity.isMultiple) {
        if (!kdmValidity.validFrom && !kdmValidity.validTill) {
          kdmValidity = {
            validFrom,
            validTill,
            timezone: timezone,
            isMultiple: false
          }
        } else {
          const curValidFrom = momentTZ.tz(validFrom, timezone),
            curValidTill = momentTZ.tz(validTill, timezone)
          const kdmValidFrom = momentTZ.tz(kdmValidity.validFrom, kdmValidity.timezone),
            kdmValidTill = momentTZ.tz(kdmValidity.validTill, kdmValidity.timezone)
          if (!_.isEqual(curValidFrom, kdmValidFrom) || !_.isEqual(curValidTill, kdmValidTill)) {
            kdmValidity = {
              validFrom: '',
              validTill: '',
              timezone: '',
              isMultiple: true
            }
          }
        }
      } else if (showValidity && !kdmValidity.isMultiple) {
        kdmValidity = {
          validFrom: '',
          validTill: '',
          timezone: '',
          isMultiple: true
        }
      }

      const theatreKdmGenerationComplete = this.getTheatreKdmStatus(theatre, kdmStatus)
      if (kdmGenerationCompleted && !theatreKdmGenerationComplete) {
        kdmGenerationCompleted = theatreKdmGenerationComplete
      }
    })

    return {
      kdmStatus,
      kdmGenerationCompleted,
      showKdmDownloadOption: theatres.length === 1 ? kdmGenerationCompleted : false,
      kdmValidity
    }
  }

  getDCPStatusSummary (DCPStatus) {
    let statusSummary = []
    let current = 1
    for (const status in DCPStatus) {
      statusSummary.push(<span key={current}
                               className={`qw-bold dcp-${status.toLowerCase()}`}>{current > 1
        ? <span>&nbsp;&bull;&nbsp;</span>
        : null}{DCPStatus[status]} {I18n.t(`compositions.dcpStatus.${status}`)}</span>)
      current += 1
    }
    return statusSummary
  }

  downloadKDMS () {
    const {composition} = this.props
    this.props.downloadKDMs(composition.name, [composition.id], composition.theatres.map(t => t.id))
  }

  getDcpShipmentStatus (dcpInfo) {
    const {shippingDetails: {trackingInfo: {status} = {}} = {}} = dcpInfo
    switch (status) {
      case ShipmentStatuses.InfoReceived:
        return ShipmentStatuses.InTransit
      case ShipmentStatuses.Pending:
        return DCPStatuses.dispatched
      default:
        return status || DCPStatuses.dispatched
    }
  }

  getCplInfo () {
    const {composition: {theatres}} = this.props
    let theatreCount = 0
    let isDcpBooking = false
    let isKdmBooking = false
    const dcpStatus = {}
    theatres.forEach(t => {
      theatreCount += 1
      if (!_.isEmpty(t.kdmSummary)) {
        isKdmBooking = true
      }
      if (t.dcp && !_.isEmpty(t.dcp)) {
        isDcpBooking = true
        let status = this.getDcpStatus(t.dcp.status)
        if (status === DCPStatuses.dispatched) {
          status = this.getDcpShipmentStatus(t.dcp.details).toLowerCase()
        }
        if (dcpStatus[status]) {
          dcpStatus[status] += 1
        } else {
          dcpStatus[status] = 1
        }
      }
    })

    return {
      isDcpBooking,
      isKdmBooking,
      theatreCount: theatreCount,
      dcpStatus
    }
  }

  getDcpStatus ({subDetail, type}) {
    let status = subDetail || (type ? type.toLowerCase() : DCPStatuses.pending)
    switch (status) {
      case DCPStatuses.error:
      case DCPStatuses.expired:
        status = DCPStatuses.failed;
        break;
      case DCPStatuses.pending_cancellation:
        status = DCPStatuses.pending;
        break;
      default:
        break
    }

    return status
  }

  renderExpandToggle () {
    return <div className='qw-p-xx-small qw-icon-medium left' onClick={this.props.toggleExpand}>
      {this.props.expand ? <ChevronDown className='qw-icon-x-small'/> : <ChevronRight
        className='qw-icon-x-small'/>}
    </div>
  }

  renderDCPStatus (theatre) {
    if (!theatre.dcp) {
      return <span>&nbsp;</span>
    }

    const {status: dcpStatus, details, deliveryType} = theatre.dcp
    let status = this.getDcpStatus(dcpStatus)
    if (status === DCPStatuses.dispatched) {
      status = this.getDcpShipmentStatus(details).toLowerCase()
    }

    let shippingInfo
    let icon
    let className = ''
    let onShowDCPInfo = null
    if (details) {
      switch (deliveryType) {
        case DCPDeliveryType.THEATRE_APPLIANCE: {
          const {progress, status} = details
          if (status === DCPStatuses.downloading) {
            shippingInfo = <span> {parseInt(progress, 10)}%</span>
          }
          icon = <DownloadIcon className="qw-icon-x-small inline qw-align-middle qw-mr-xx-small"/>
          break
        }
        case DCPDeliveryType.PARTNERS: {
          const {shippingDetails: {trackingId, name: trackingName}} = details
          if (trackingName && trackingId) {
            className = 'qw-cursor-pointer'
            onShowDCPInfo = this.showDCPInfo.bind(this, theatre)
            shippingInfo = <span>{trackingName} &bull; {trackingId}</span>
          }
          icon = <DriveIcon className="qw-icon-x-small inline qw-align-middle qw-mr-xx-small"/>
          break
        }
        default:
          shippingInfo = ''
      }
    }
    return (
      <div className={className} onClick={onShowDCPInfo}>
        {icon} <span
        className={`qw-bold dcp-${status.toLowerCase()} qw-mb-small qw-align-middle`}>{I18n.t(`compositions.dcpStatus.${status}`)}</span>
        {(this.state.loadingTheatreId === theatre.id) &&
        <span className='inline-block qw-align-middle qw-icon-x-small'>
          <LoaderIcon className='qw-icon-x-small'/></span>}
        {shippingInfo &&
        <span className={cx('block', {'qw-pl-x-large': icon})}>{shippingInfo}</span>}
      </div>
    )
  }

  renderTheatreInfo (cplInfo) {
    const {composition} = this.props
    return composition.theatres.sort((
      a, b) => naturalCompare(a.name.toLowerCase(), b.name.toLowerCase()))
      .map((theatre, idx) =>
        <CompositionTheatreItem
          theatre={theatre} key={idx} index={idx} cplInfo={cplInfo}
          getTheatreKdmStatus={this.getTheatreKdmStatus.bind(this)}
          renderKDMStatusSummary={this.renderKDMStatusSummary.bind(this)}
          renderDCPStatus={this.renderDCPStatus.bind(this)}
          showKDMInfo={this.showKDMInfo.bind(this)}
          showDCPInfo={this.showDCPInfo.bind(this)}
          showKdmValidity={this.showKdmValidity.bind(this)}
        />
      )
  }

  renderKdmOptions ({kdmStatus, theatreCount, kdmGenerationCompleted, showKdmDownloadOption}) {
    if (!kdmGenerationCompleted || !showKdmDownloadOption) {
      return
    }
    const theatre = this.props.composition.theatres[0]
    return (
      <span className='inline-block'>
        <Popover content={I18n.t('compositions.downloadKDMs')} overlayClassName='cpl-popover'>
          <span onClick={() => this.downloadKDMS()}
                className='qw-justify-center qw-cursor-pointer qw-px-xx-small'>
            <DownloadKDMIcon className="qw-icon-small inline qw-align-middle"/>
          </span>
        </Popover>
        <Popover content={I18n.t('compositions.emailKDMS')} overlayClassName='cpl-popover'>
          <span onClick={() => this.showKDMInfo(theatre, kdmGenerationCompleted)}
                className={cx('qw-justify-center qw-cursor-pointer qw-px-xx-small', {
                  'qw-display-none': this.props.user.info.role_in_product.code === DEVICE_MANAGER_ROLE})}>
            <EmailIcon className="qw-icon-small inline qw-align-middle"/>
          </span>
        </Popover>
      </span>
    )
  }

  isAttributesAvailable () {
    const {composition: {attributes} = {}} = this.props
    if (!attributes || _.isEmpty(attributes)) {
      return false
    }

    let dataCount = 0
    const displayAttrArray = Object.keys(CPLAttributes)
    Object.keys(attributes).forEach(attr => {
      if (attributes[attr].value && displayAttrArray.includes(attributes[attr].type)) {
        dataCount += 1
      }
    })

    return dataCount > 0
  }

  render () {
    const {composition} = this.props
    const isMarkerAvailable = !_.isEmpty(composition.markers) &&
      (composition.markers.ffec || composition.markers.ffmc)
    const isAttributesAvailable = this.isAttributesAvailable()
    const cplInfo = this.getCplInfo()
    const kdmInfo = cplInfo.isKdmBooking ? this.getKDMSummaryData() : {}

    return (
      <Card className="composition-card qw-m-small" bordered={false}>
        <Row>
          <Col span={22}>
            <p
              className="name qw-color-text-body qw-font-size-body qw-bold qw-line-height-body qw-truncate"
              title={composition.name}>
              {composition.name}</p>
          </Col>
          <Col span={2}>
            <h3
              className="duration qw-right-align qw-color-text-body qw-font-size-body qw-bold qw-line-height-body">
              {composition.duration ? getDuration(composition.duration) : null}</h3>
          </Col>
        </Row>
        {(isMarkerAvailable || isAttributesAvailable) &&
        <Row className='qw-mt-x-small'>
          <Col span={20}>
            {isAttributesAvailable ? <CompositionAttributes composition={composition}/> :
              <span>&nbsp;</span>}
          </Col>
          {isMarkerAvailable && <Col span={4}>
            <div onClick={() => this.showViewCreditOffset(composition)}
                 className="qw-color-text-link qw-color- qw-right-align qw-cursor-pointer qw-font-size-small qw-line-height-small">
              {I18n.t('compositions.viewCreditOffset',
                {value: this.showCreditOffset(composition.duration, composition.markers.ffec)})}
            </div>
          </Col>}
        </Row>}
        <Row
          className='qw-mt-small qw-mb-xx-small qw-color-text-body qw-font-size-small qw-bold qw-line-height-medium'>
          <Col span={6} className='qw-cursor-pointer'>
            {this.renderExpandToggle()}
            <span onClick={this.props.toggleExpand}>
              {cplInfo.theatreCount} {pluralize(I18n.t('compositions.theatre'), cplInfo.theatreCount)}</span>
          </Col>
          {cplInfo.isDcpBooking &&
          <Col span={9} className='qw-pl-x-small qw-cursor-pointer'
               onClick={this.props.toggleExpand}>{I18n.t('compositions.dcp')}</Col>}
          {cplInfo.isKdmBooking &&
          <Col span={9} className='qw-pl-x-small'><span className='qw-mr-xx-small qw-cursor-pointer'
                                                        onClick={this.props.toggleExpand}>
              {I18n.t('compositions.kdm')}</span>{this.renderKdmOptions(kdmInfo)}</Col>}
        </Row>
        {this.props.expand && this.renderTheatreInfo(cplInfo)}
        {!this.props.expand &&
        <Row className='qw-font-size-small qw-line-height-small'>
          <Col span={6} className='qw-pl-large'>&nbsp;</Col>
          {cplInfo.isDcpBooking && <Col span={9} className='qw-pl-x-small qw-cursor-pointer'
                                        onClick={this.props.toggleExpand}>
            {this.getDCPStatusSummary(cplInfo.dcpStatus)}</Col>}
          {cplInfo.isKdmBooking &&
          <Col span={9} className='kdm-summary qw-color-text-body qw-pl-x-small qw-cursor-pointer'
               onClick={this.props.toggleExpand}>
            {this.renderKDMStatusSummary(kdmInfo.kdmStatus, kdmInfo.kdmValidity)}</Col>}
        </Row>}
      </Card>
    )
  }
}

export default Composition



// WEBPACK FOOTER //
// ./src/app/components/compositions/Composition.js